import { createContext, useCallback, useContext, useState, ReactNode } from 'react'
import { CheckCircle2, XCircle, Info, X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'info'
interface ToastItem {
  id: number
  type: ToastType
  message: string
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let idCounter = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = ++idCounter
      setToasts((prev) => [...prev, { id, type, message }])
      setTimeout(() => remove(id), 4000)
    },
    [remove]
  )

  const value: ToastContextValue = {
    showToast,
    success: (message: string) => showToast(message, 'success'),
    error: (message: string) => showToast(message, 'error'),
    info: (message: string) => showToast(message, 'info'),
  }

  const icon = (type: ToastType) => {
    if (type === 'success') return <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
    if (type === 'error') return <XCircle className="w-5 h-5 text-red-600 shrink-0" />
    return <Info className="w-5 h-5 text-blue-600 shrink-0" />
  }

  const border = (type: ToastType) =>
    type === 'success' ? 'border-green-200 bg-green-50' : type === 'error' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-start gap-3 border rounded-lg shadow-lg px-4 py-3 animate-in fade-in slide-in-from-top-2 ${border(t.type)}`}
            role="status"
          >
            {icon(t.type)}
            <p className="text-sm text-neutral-800 flex-1">{t.message}</p>
            <button onClick={() => remove(t.id)} className="text-neutral-400 hover:text-neutral-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

/** Best-effort extraction of a readable message from an Axios/DRF error.
 * DRF validation errors come back as {"field_name": ["message", ...], ...}
 * or {"non_field_errors": [...]} or {"detail": "..."}. This surfaces the
 * actual field name(s) involved instead of a generic "Something went
 * wrong", e.g. "date: This field is required."
 *
 * IMPORTANT: when a request was made with `responseType: 'blob'` (file
 * downloads — exports, templates, etc.), an *error* response body also
 * arrives as an unreadable Blob, not parsed JSON — axios doesn't know to
 * treat it differently just because the request failed. Reading `.detail`
 * off a Blob silently does nothing, so every export error used to fall
 * through to the generic fallback message no matter what the backend
 * actually said. Callers that use blob responses should first run the
 * error through `extractBlobErrorData()` (see below) and pass the result
 * in as `data` via `getErrorMessage(err, fallback, alreadyExtractedData)`.
 */
export function getErrorMessage(err: any, fallback = 'Something went wrong. Please try again.', blobExtractedData?: any): string {
  const data = blobExtractedData ?? err?.response?.data
  if (!data) return err?.message || fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail

  const parts: string[] = []
  for (const [field, value] of Object.entries(data)) {
    const messages = Array.isArray(value) ? value : [value]
    const text = messages.filter(Boolean).join(' ')
    if (!text) continue
    if (field === 'non_field_errors' || field === '__all__') {
      parts.push(text)
    } else {
      parts.push(`${field}: ${text}`)
    }
  }
  return parts.length > 0 ? parts.join(' | ') : fallback
}

/** For requests made with `responseType: 'blob'`: on a failed request, the
 * server's JSON error body arrives as a Blob (not parsed JSON) because
 * axios doesn't inspect the response before deciding how to parse it — it
 * just honors whatever `responseType` you asked for, success or failure.
 * This reads that Blob back out as text and parses it, so the real
 * backend error message can actually reach the user instead of a generic
 * "export failed" toast every time. Returns null if the body isn't JSON
 * (e.g. a genuine network failure, or an HTML error page from a proxy).
 */
export async function extractBlobErrorData(err: any): Promise<any | null> {
  const blob = err?.response?.data
  if (!(blob instanceof Blob)) return null
  try {
    const text = await blob.text()
    return JSON.parse(text)
  } catch {
    return null
  }
}


// import { createContext, useCallback, useContext, useState, ReactNode } from 'react'
// import { CheckCircle2, XCircle, Info, X } from 'lucide-react'

// type ToastType = 'success' | 'error' | 'info'
// interface ToastItem {
//   id: number
//   type: ToastType
//   message: string
// }

// interface ToastContextValue {
//   showToast: (message: string, type?: ToastType) => void
//   success: (message: string) => void
//   error: (message: string) => void
//   info: (message: string) => void
// }

// const ToastContext = createContext<ToastContextValue | null>(null)

// let idCounter = 0

// export function ToastProvider({ children }: { children: ReactNode }) {
//   const [toasts, setToasts] = useState<ToastItem[]>([])

//   const remove = useCallback((id: number) => {
//     setToasts((prev) => prev.filter((t) => t.id !== id))
//   }, [])

//   const showToast = useCallback(
//     (message: string, type: ToastType = 'info') => {
//       const id = ++idCounter
//       setToasts((prev) => [...prev, { id, type, message }])
//       setTimeout(() => remove(id), 4000)
//     },
//     [remove]
//   )

//   const value: ToastContextValue = {
//     showToast,
//     success: (message: string) => showToast(message, 'success'),
//     error: (message: string) => showToast(message, 'error'),
//     info: (message: string) => showToast(message, 'info'),
//   }

//   const icon = (type: ToastType) => {
//     if (type === 'success') return <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
//     if (type === 'error') return <XCircle className="w-5 h-5 text-red-600 shrink-0" />
//     return <Info className="w-5 h-5 text-blue-600 shrink-0" />
//   }

//   const border = (type: ToastType) =>
//     type === 'success' ? 'border-green-200 bg-green-50' : type === 'error' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'

//   return (
//     <ToastContext.Provider value={value}>
//       {children}
//       <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-full max-w-sm">
//         {toasts.map((t) => (
//           <div
//             key={t.id}
//             className={`flex items-start gap-3 border rounded-lg shadow-lg px-4 py-3 animate-in fade-in slide-in-from-top-2 ${border(t.type)}`}
//             role="status"
//           >
//             {icon(t.type)}
//             <p className="text-sm text-neutral-800 flex-1">{t.message}</p>
//             <button onClick={() => remove(t.id)} className="text-neutral-400 hover:text-neutral-600">
//               <X className="w-4 h-4" />
//             </button>
//           </div>
//         ))}
//       </div>
//     </ToastContext.Provider>
//   )
// }

// export function useToast() {
//   const ctx = useContext(ToastContext)
//   if (!ctx) throw new Error('useToast must be used within ToastProvider')
//   return ctx
// }

// /** Best-effort extraction of a readable message from an Axios/DRF error.
//  * DRF validation errors come back as {"field_name": ["message", ...], ...}
//  * or {"non_field_errors": [...]} or {"detail": "..."}. This surfaces the
//  * actual field name(s) involved instead of a generic "Something went
//  * wrong", e.g. "date: This field is required." */
// export function getErrorMessage(err: any, fallback = 'Something went wrong. Please try again.'): string {
//   const data = err?.response?.data
//   if (!data) return err?.message || fallback
//   if (typeof data === 'string') return data
//   if (data.detail) return data.detail

//   const parts: string[] = []
//   for (const [field, value] of Object.entries(data)) {
//     const messages = Array.isArray(value) ? value : [value]
//     const text = messages.filter(Boolean).join(' ')
//     if (!text) continue
//     if (field === 'non_field_errors' || field === '__all__') {
//       parts.push(text)
//     } else {
//       parts.push(`${field}: ${text}`)
//     }
//   }
//   return parts.length > 0 ? parts.join(' | ') : fallback
// }